// Acesso a dados de `project_photos` (Etapa 11 — spec S02/S05 §Fotos).
// Leitura é `select` direto (Drizzle), como em projectRepository.ts.
//
// Escrita é a EXCEÇÃO deliberada ao padrão de src/db/writes.ts (ver
// ProjectPhotosSection para o fluxo completo, e o brief da Etapa 11 para a
// decisão): a spec S05 exige que a linha `project_photos` só entre na
// outbox DEPOIS que o upload ao Storage já concluiu, nunca antes (nunca
// referenciar um arquivo que ainda não existe lá). Por isso NÃO existe aqui
// um "insertPendingPhoto" — o estado "enviando" (arquivo local, badge) vive
// só em memória na UI (ProjectPhotosSection), e `recordUploadedPhoto` abaixo
// só é chamada depois que o upload já teve sucesso. Nesse ponto o arquivo já
// existe no Storage, então usar `insertRecord` normalmente (grava + enfileira
// outbox na mesma transação) é seguro e correto — não é preciso nenhum
// helper novo em writes.ts.
import { and, eq, isNull } from 'drizzle-orm';
import { projectPhotos, type ProjectPhoto } from '../../db/schema';
import { insertRecord, softDeleteRecord } from '../../db/writes';
import type { AppDatabase } from '../../db/outbox';

/** Fotos não excluídas (soft delete) de um projeto. */
export async function listProjectPhotos(db: AppDatabase, projectId: string): Promise<ProjectPhoto[]> {
  return db
    .select()
    .from(projectPhotos)
    .where(and(eq(projectPhotos.projectId, projectId), isNull(projectPhotos.deletedAt)));
}

export interface RecordUploadedPhotoParams {
  projectId: string;
  storagePath: string;
  takenAt?: Date;
}

/**
 * Grava a linha DEFINITIVA de `project_photos` — só deve ser chamada depois
 * que o upload ao Storage já concluiu com sucesso (ver comentário do módulo
 * acima). `takenAt` é opcional (a coluna tem default "agora" no schema).
 */
export async function recordUploadedPhoto(db: AppDatabase, params: RecordUploadedPhotoParams): Promise<ProjectPhoto> {
  // Atribuído a uma variável antes de insertRecord — mesma pegadinha de
  // excess property check contra o generic WritableTable documentada em
  // src/db/writes.test.ts e reaproveitada em linkSupply.ts.
  const newPhotoData: { projectId: string; storagePath: string; takenAt?: Date } = {
    projectId: params.projectId,
    storagePath: params.storagePath,
    ...(params.takenAt ? { takenAt: params.takenAt } : {}),
  };
  return insertRecord(db, projectPhotos, newPhotoData);
}

/** Soft delete (regra invíolável #3) — a remoção física do arquivo no
 * Storage fica para o purge completo da Etapa 15 (fora do escopo desta
 * task, ver brief). */
export async function deleteProjectPhoto(db: AppDatabase, id: string): Promise<ProjectPhoto> {
  return softDeleteRecord(db, projectPhotos, id);
}
